import { useState, useCallback, useRef, lazy, Suspense, forwardRef, useImperativeHandle } from 'react'
import { Loader2 } from 'lucide-react'
import { CollabStatusBadge } from './CollabStatusBadge.js'
import { EditorShareMenu } from './EditorShareMenu.js'
import { EditorToolbar } from './EditorToolbar.js'
import { ConflictOverlay } from './ConflictOverlay.js'
import type { EditorEditHandle, EditorUndoRedo } from '../client/editor-registry.js'

const LazyUniverModifier = lazy(() =>
  import('./UniverModifier.js').then(m => ({ default: m.UniverModifier }))
)

const LoadingSpinner = (
  <div className="flex-1 flex items-center justify-center">
    <Loader2 className="animate-spin themed-text-muted" size={24} />
  </div>
)

interface ExcelEditorProps {
  room: string
  fileId: string
  fileName?: string
  isActive?: boolean
  onDirtyChange?: (dirty: boolean) => void
  onConflictChange?: (conflicted: boolean) => void
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  registerUndoRedo?: (editor: EditorUndoRedo) => () => void
  initialViewMode?: 'preview' | 'source'
  onViewModeChange?: (mode: 'preview' | 'source') => void
  scrollRef?: React.RefObject<HTMLElement | null>
  scrollSyncTrigger?: number
}

export const ExcelEditor = forwardRef<EditorEditHandle, ExcelEditorProps>(
  function ExcelEditor({ room, fileId, fileName, isActive, onDirtyChange, onConflictChange, onSaveRef, onDiscardRef, registerUndoRedo }, ref) {
    const [connected, setConnected] = useState(false)
    const [isConflicted, setIsConflicted] = useState(false)
    const [conflictGeneration, setConflictGeneration] = useState(0)
    const editorRef = useRef<EditorEditHandle>(null)
    const forceSaveRef = useRef<(() => void) | null>(null)
    void onDiscardRef?.current

    const handleConnectedChange = useCallback((c: boolean) => setConnected(c), [])
    const handleConflictChange = useCallback((c: boolean) => {
      setIsConflicted(prev => {
        if (prev && c) setConflictGeneration(n => n + 1)
        return c
      })
    }, [])

    useImperativeHandle(ref, () => ({
      copy: () => editorRef.current?.copy(),
      cut: () => editorRef.current?.cut(),
      paste: () => editorRef.current?.paste(),
      delete: () => editorRef.current?.delete(),
      getSelection: () => editorRef.current?.getSelection?.() ?? null,
      focusEditor: () => editorRef.current?.focusEditor?.(),
    }), [])

    return (
      <ConflictOverlay
        isConflicted={isConflicted}
        conflictGeneration={conflictGeneration}
        fileId={fileId}
        onDiscard={() => onDiscardRef?.current?.()}
        onOverwrite={() => forceSaveRef.current?.()}
        onConflictChange={onConflictChange}
        disabled={false}
        renderSavedVersion={(content, loading) => (
          loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="animate-spin themed-text-muted" size={24} />
            </div>
          ) : (
            <Suspense fallback={LoadingSpinner}>
              <LazyUniverModifier
                room=""
                fileName={fileName}
                mode="readonly"
                previewData={content ? new TextEncoder().encode(content).buffer : undefined}
              />
            </Suspense>
          )
        )}
      >
        {/* Toolbar */}
        <EditorToolbar status={<><EditorShareMenu fileId={fileId} fileName={fileName} /><CollabStatusBadge connected={connected} /></>} />

        {/* Editor */}
        <Suspense fallback={LoadingSpinner}>
          <LazyUniverModifier
            ref={editorRef}
            room={room}
            fileId={fileId}
            fileName={fileName}
            isActive={isActive}
            onDirtyChange={onDirtyChange}
            onSaveRef={onSaveRef}
            onDiscardRef={onDiscardRef}
            registerUndoRedo={registerUndoRedo}
            onConnectedChange={handleConnectedChange}
            onConflictChange={handleConflictChange}
            onForceSaveRef={forceSaveRef}
          />
        </Suspense>
      </ConflictOverlay>
    )
  }
)
